import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'
import * as componentStore from '../component-store'
import * as overflowMenu from '../overflow-menu'

/**
 * Displays table widget
 */
@ngCore.Component({
  imports: [
    ds.table.Component,
    overflowMenu.Component,
    ds.illustration.emptyState.Component,
  ],
  selector: 'atn-mosaic-table',
  standalone: true,
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link AtnMosaicWidgetStore}*/
  public readonly widgetStore = ngCore.inject(
    componentStore.Widget<ents.core.mosaic.Widget.Type.Table>,
  )
  /** @see {@link state.dms.schema.Feature} */
  private readonly _currentRenderingFeature = ngCore.inject(
    state.mosaic.endUser.currentRendering.Feature,
  )
  /** @see {@link state.mosaic.dynamicEcs.Feature} */
  private readonly _dynamicEcsFeature = ngCore.inject(
    state.mosaic.dynamicEcs.Feature,
  )
  /** @see {@link state.variableOverrides.Feature} */
  private readonly _variableOverridesFeature = ngCore.inject(
    state.variableOverrides.Feature,
  )
  /**
   * The original page widget reference used when the dynamic collection is
   * bootstrapped. The rendered widget ID is not interchangeable with this
   * reference's flexible identifier (the latter can be an alias), so the
   * lookup uses the same reference whenever it is available.
   */
  private readonly _widgetRefSg = ngCore.computed(
    (refId = this.widgetStore.refIdSg()) =>
      refId
        ? this._currentRenderingFeature.subfeatures.widgets.selectors
            .getWidgetRef(refId)
            .asSignal()
        : undefined,
  )
  /** The dynamic collection to display in this widget. */
  public readonly collectionSg = ngCore.computed(
    (
      ref = this._widgetRefSg(),
      refId = this.widgetStore.refIdSg(),
      widgetId = this.widgetStore.renderingIdSg(),
    ) => {
      // Modal, slideout, and preview widgets do not belong to the page's
      // widget-reference lookup. Keep a lightweight fallback reference for
      // those contexts; the dynamic-EC selector matches it by ref ID when an
      // exact widget identifier is unavailable.
      const lookupRef =
        ref ??
        (refId ? { id: refId, widget: { id: widgetId ?? refId } } : undefined)

      return lookupRef
        ? this._dynamicEcsFeature.selectors.getDynamicEc(lookupRef).asSignal()
        : undefined
    },
  )

  /**
   * Canonical EC table input for this widget.
   *
   * The dynamic collection still owns query, selection, pagination, and
   * attribute state. The view supplies only the presentation options that the
   * old widget passed as independent table inputs; custom projected actions
   * remain in the template because they are Angular content rather than table
   * action descriptors.
   */
  public readonly tableConfigSg = ngCore.computed(() => {
    const collection = this.collectionSg()
    if (!collection) {
      return undefined
    }

    const createAction = this.widgetStore.contentSg()?.onCreate
    const pageContext =
      this._currentRenderingFeature.selectors.context.asSignal()
    const widgetId = this.widgetStore.renderingIdSg()
    const refId = this.widgetStore.refIdSg()

    return {
      collection,
      view: {
        creation:
          createAction === undefined
            ? undefined
            : () => {
                // Preserve the legacy widget's no-op behavior for the
                // unimplemented automatic creation mode.
                if (createAction === 'auto') {
                  return
                }

                if (pageContext && widgetId) {
                  this._currentRenderingFeature.subfeatures.actions
                    .createActionHandler('Mosaic.Table.CreateBtn.Clicked', {
                      ...pageContext,
                      actionRef: createAction,
                      widget: {
                        id: widgetId,
                        refId,
                      },
                    })
                    .execute()
                }
              },
        pagination: true,
        search: true,
        title: this.widgetStore.nameSg(),
      },
    } satisfies ds.table.ec.Config.Input.ForTable
  })

  /**
   * Returns true if table widget has variable definitions
   */
  public hasVariablesSg = ngCore.computed(
    (widgetId = this.widgetStore.renderingIdSg()) =>
      !!widgetId &&
      this._variableOverridesFeature.selectors
        .hasDefinitions({ widget: { widgetId } })
        .asSignal(),
  )

  /**
   * Returns true if table widget has applied variable values
   */
  public hasAppliedVariableValuesSg = ngCore.computed(
    (
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
      context = this._currentRenderingFeature.selectors.context.asSignal(),
    ) =>
      !!(
        context &&
        widgetId &&
        this._variableOverridesFeature.selectors
          .hasAppliedValues({
            widget: {
              appId: context.appId,
              pageId: context.pageId,
              refId,
              scenarioId: context.scenarioId,
              widgetId,
            },
          })
          .asSignal()
      ),
  )

  /**
   * Open variables slideout for current widget
   */
  public openVariablesSlideoutHandler(): void {
    const context = this._currentRenderingFeature.selectors.context.asSignal()
    const widgetId = this.widgetStore.renderingIdSg()
    const refId = this.widgetStore.refIdSg()
    if (context && widgetId) {
      const { appId, pageId, scenarioId } = context
      this._variableOverridesFeature.actions.setFocused
        .from('Mosaic.Table.FiltersBtn.Clicked')
        .dispatch({
          widget: {
            appId,
            pageId,
            refId,
            scenarioId,
            widgetId,
          },
        })
    }
  }
}
